import i18nModule from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "@src/locales/en.json";
import de from "@src/locales/de.json";
import enProject from "@src/locales/en/project.json";
import deProject from "@src/locales/de/project.json";

/**
 * Unwraps the i18next instance from what module federation hands us.
 *
 * `@originjs/vite-plugin-federation` resolves a shared module to its ES module
 * NAMESPACE, not to its default export: the built code is
 * `const i18n = await importShared("i18next")`. i18next's namespace re-exports
 * a handful of instance methods bound to the singleton (`use`, `init`, `t`,
 * `changeLanguage`), which is why calling `i18n.use(...).init(...)` kept
 * working and hid this for so long. It does NOT carry `on`, `isInitialized` or
 * `addResourceBundle`.
 *
 * Two consequences, both real:
 *
 *  1. `i18n.isInitialized` was always `undefined`, so the "only init when
 *     standalone" guard below never fired inside the shell and every module
 *     re-initialised the shared instance, replacing the resources of the
 *     modules that had loaded before it. Exactly what that guard exists to
 *     prevent.
 *  2. Passing the namespace to `<I18nextProvider i18n={...}>` blows up as
 *     `TypeError: i18n?.on is not a function`, because react-i18next
 *     subscribes with `i18n.on(...)`. That is what surfaced this.
 *
 * The shell has carried this same unwrap since before the modules did.
 */
export function resolveI18nInstance<T>(module: T): T {
  const candidate = module as { on?: unknown; default?: unknown };

  // Already the instance (standalone dev, and under vitest).
  if (typeof candidate?.on === "function") return module;

  // A namespace from importShared: the instance is the default export.
  const unwrapped = candidate?.default;
  if (typeof (unwrapped as { on?: unknown } | undefined)?.on === "function") {
    return unwrapped as T;
  }

  // Neither shape. Return it unchanged so the failure surfaces where it is
  // caused rather than as a confusing undefined further downstream.
  return module;
}


/**
 * i18n for the extraction module.
 *
 * Every module used to merge its bundle into the single default `translation`
 * namespace of the one shared i18next instance. Keys collided silently across
 * modules (`common.error` existed in three of them), and because the shell
 * prefetches every permitted module on idle, which module's value won was
 * decided by load order and was not deterministic.
 *
 * This module now owns the `extraction` namespace. Call sites are unchanged: the
 * module root wraps its tree in `<I18nextProvider defaultNS="extraction">`, and
 * react-i18next resolves a bare `useTranslation()` as
 * `ns || defaultNS-from-context || i18n.options.defaultNS`, so every existing
 * `t("...")` inside the module reads from `extraction` without a per-call-site edit.
 *
 * The shell and BeeHive's UATAuthProvider keep the default `translation`
 * namespace. Do not add module keys there.
 */
export const I18N_NAMESPACE = "extraction";

const i18n = resolveI18nInstance(i18nModule);


if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { [I18N_NAMESPACE]: en, project: enProject },
        de: { [I18N_NAMESPACE]: de, project: deProject },
      },
      defaultNS: I18N_NAMESPACE,
      fallbackLng: "en",
      interpolation: { escapeValue: false },
      react: { useSuspense: true },
    });
} else {
  i18n.addResourceBundle("en", I18N_NAMESPACE, en, true, true);
  i18n.addResourceBundle("de", I18N_NAMESPACE, de, true, true);
  i18n.addResourceBundle("en", "project", enProject, true, true);
  i18n.addResourceBundle("de", "project", deProject, true, true);
}

export default i18n;
